// Актуализация цен на перелёты: типы контракта с сервером.
//
// Контракт заморожен и повторяет server/prices/*: клиент ничего не додумывает
// и ничего не считает за сервер. Сравниваются только одинаковые рейсы —
// перевозчик, номер и дата совпадают, — это гарантирует сервер, а не мы.
import type { CurrencyDef, FlightSegment, PriceScope, Segment } from '../types';

/** Откуда пришла котировка. Провайдеры включаются по одному в конфиге сервера. */
export type PriceProviderName = 'travelpayouts' | 'websearch' | 'page';

/**
 * Чем кончилась проверка одного рейса. `not_found` — штатный исход, а не сбой:
 * рейс мог сняться с продажи или просто не найтись у включённых провайдеров.
 */
export type PriceCheckStatus = 'ok' | 'not_found' | 'error';

export interface PriceQuote {
  provider: PriceProviderName;
  /** Кто показывает цену — домен агрегатора или авиакомпании. */
  site: string;
  priceMinor: number;
  currency: string;
  /** Та же цена, пересчитанная в валюту самого рейса: сравнивать можно только это. */
  normMinor: number;
  url: string;
  /** Найден ровно тот рейс (перевозчик + номер + дата), а не похожий. */
  exact: boolean;
  foundAt: number;
  note: string;
}

export interface PriceCheck {
  id: number;
  segmentId: string;
  /** `check` — проверка цены, `apply` — отметка «эту цену поставили в блок». */
  kind: 'check' | 'apply';
  checkedAt: number;
  route: string;
  departDate: string;
  carrier: string;
  flightNo: string;
  scope: string;
  /** Цена блока на момент проверки — база сравнения, записанная сервером. */
  baseMinor: number;
  baseCurrency: string;
  /** Лучшая найденная цена в валюте блока; null — сравнивать не с чем. */
  bestMinor: number | null;
  bestSite: string;
  status: PriceCheckStatus;
  note: string;
  quotes: PriceQuote[];
}

/**
 * Что `/api/prices/config` отвечает КОМУ УГОДНО, включая гостя. Ровно одно
 * поле: «есть ли в продукте проверка цен» — не секрет, это витрина.
 */
export interface PublicPriceConfig {
  enabled: boolean;
}

/**
 * Что добавляется вошедшему. Гостю этого не видно намеренно: готовность
 * источников рассказала бы анониму про наш деплой (какие токены заведены), а
 * лимит и остаток — это про конкретный аккаунт.
 */
export interface FullPriceConfig extends PublicPriceConfig {
  providers: { travelpayouts: boolean; websearch: boolean; page: boolean };
  /** Сколько рейсов сервер берёт за один запрос; лишнее он молча обрежет. */
  maxFlights: number;
  dailyLimit: number | null;
  remaining: number | null;
}

/**
 * Две разные формы одного ответа, а не одна с необязательными полями:
 * необязательные поля только перенесли бы проверку в каждое место вызова, а
 * тут компилятор сам не даст прочитать `maxFlights`, пока не доказано, что
 * ответ полный. Доказывает `isFullConfig()`.
 */
export type PriceConfig = PublicPriceConfig | FullPriceConfig;

/**
 * Проверка по факту присланного, а не по «мы вроде вошли»: единственный
 * надёжный признак полного ответа — что нужные поля в нём действительно есть.
 */
export function isFullConfig(config: PriceConfig | null): config is FullPriceConfig {
  return config !== null && typeof (config as FullPriceConfig).maxFlights === 'number';
}

/**
 * Рейс в том виде, в каком он уходит на проверку. Это не `FlightSegment`:
 * лишних полей серверу знать незачем, а `url` и `linkedDepartLocal` — пустые
 * строки, когда данных нет (поле ссылки у рейса необязательное, и ничто в
 * проверке от него зависеть не должно).
 */
export interface PriceFlightInput {
  segmentId: string;
  from: string;
  to: string;
  departLocal: string;
  arriveLocal: string;
  carrier: string;
  flightNumber: string;
  priceMinor: number;
  currency: string;
  priceScope: PriceScope;
  /** '' — ссылки у рейса нет. */
  url: string;
  /** '' — парного обратного рейса нет. */
  linkedDepartLocal: string;
}

/** Тело POST /api/prices/check. Справочник валют нужен серверу для пересчёта. */
export interface PriceCheckRequest {
  flights: PriceFlightInput[];
  currencies: CurrencyDef[];
}

export interface PriceCheckResponse {
  checks: PriceCheck[];
  /** Заметки обо всём запуске целиком (что пропущено и почему). */
  notes: string[];
}

/** Тело POST /api/prices/apply — запись в историю, а не смена цены блока. */
export interface PriceApplyInput {
  segmentId: string;
  fromMinor: number;
  toMinor: number;
  currency: string;
  site: string;
  url: string;
}

/**
 * Публичное API модуля. Всё, что нужно знать зовущему интерфейсу.
 *
 * Доступность раздвоена намеренно — см. `availability.ts`. Компоненты модуля
 * сами возвращают null там, где им не место, поэтому оборачивать их
 * проверками не нужно.
 */
export interface PricesApi {
  config: PriceConfig | null;
  /**
   * Фича есть, и её место на экране занято. У гостя это true: на демо-поездке
   * витрины кнопка обязана быть видна — иначе о существовании проверки цен
   * никто не узнает. Что она при этом не проверяет, а зовёт вход, — забота
   * самой кнопки.
   */
  visible: boolean;
  /**
   * Этот человек может запустить проверку прямо сейчас: фича включена И вход
   * выполнен. Всё, что действительно ходит на сервер, смотрит только сюда.
   */
  ready: boolean;
  /** Последняя проверка рейса; null — рейс ещё ни разу не проверяли. */
  checkOf(segmentId: string): PriceCheck | null;
  busy(segmentId: string): boolean;
  /**
   * Принимает любые блоки, а не только рейсы: у варианта в руках лежит
   * `Segment[]`, и заставлять каждого зовущего фильтровать его самому — способ
   * получить пять разных фильтров. Проверяются всё равно только `kind:'flight'`.
   *
   * `owner` — метка того, кто затеял проверку (обычно `useId()` компонента).
   * Нужна ровно для одного: показать сообщение о неудаче один раз, у той самой
   * кнопки, а не у всех сразу. Не передали — сообщение не покажет никто, и
   * ошибка останется только в `error`.
   */
  refresh(segments: readonly Segment[], owner?: string): Promise<void>;
  /**
   * Записывает в историю, что цену котировки поставили в блок. Саму цену
   * блока НЕ меняет — это делает вызывающий через `useActions()`.
   * Подробнее о разделении — в README модуля.
   */
  applyQuote(segment: FlightSegment, quote: PriceQuote, owner?: string): Promise<void>;
  /** Последняя ошибка, уже переведённая; null — всё в порядке. */
  error: string | null;
  /**
   * Ошибка, если её вызвал именно этот `owner`, иначе null.
   *
   * Ошибка одна на весь модуль (она в контексте), а кнопок на доске — десятки.
   * Читать напрямую `error` в компоненте нельзя: каждая смонтированная кнопка
   * нарисует свою копию сообщения, и одна неудача превратится в два десятка
   * одинаковых всплывашек и столько же объявлений для скринридера. Показывает
   * сообщение тот, кто действие и затеял, — он и спрашивает через `errorFor`.
   */
  errorFor(owner: string): string | null;
}
